import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Camera, Upload, CheckCircle, AlertCircle, Loader2, Image as ImageIcon, Download, X } from 'lucide-react';

const GuestUpload = () => {
  const { id, slug } = useParams();
  const [event, setEvent] = useState(null);
  
  // Upload State
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploadedBy, setUploadedBy] = useState('');
  const [status, setStatus] = useState('idle'); // idle, uploading, success, error
  const [errorMsg, setErrorMsg] = useState('');

  // Gallery State
  const [activeTab, setActiveTab] = useState('upload'); // 'upload' | 'gallery'
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [loadingGallery, setLoadingGallery] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:11200';

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const res = await axios.get(`${apiUrl}/${slug}/api/events/${id}`);
        setEvent(res.data);
      } catch (err) {
        console.error('Error fetching event details', err);
      }
    };
    if (id && slug) {
      fetchEvent();
    }
  }, [id, slug, apiUrl]);

  // Gallery Fetching Effect
  useEffect(() => {
    if (activeTab === 'gallery' && id) {
      const fetchPhotos = async () => {
        setLoadingGallery(true);
        try {
          const res = await axios.get(`${apiUrl}/api/events/${id}/photos`);
          setGalleryPhotos(res.data);
        } catch (err) {
          console.error('Error fetching gallery', err);
        } finally {
          setLoadingGallery(false);
        }
      };
      
      fetchPhotos();
      const interval = setInterval(fetchPhotos, 10000); // 10s auto-refresh
      return () => clearInterval(interval);
    }
  }, [activeTab, id, apiUrl]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setPreview(URL.createObjectURL(selectedFile));
      setStatus('idle');
      setErrorMsg('');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      setErrorMsg('Por favor, selecciona una foto.');
      return;
    }

    setStatus('uploading');
    const formData = new FormData();
    formData.append('photo', file);
    if (uploadedBy) {
      formData.append('uploadedBy', uploadedBy);
    }

    try {
      await axios.post(`${apiUrl}/api/events/${id}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setStatus('success');
      setFile(null);
    } catch (err) {
      console.error(err);
      setStatus('error');
      setErrorMsg(err.response?.data?.error || 'Error al subir la foto. Intenta nuevamente.');
    }
  };

  const resetUpload = () => {
    setFile(null);
    setPreview(null);
    setStatus('idle');
    setUploadedBy('');
    setErrorMsg('');
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col items-center justify-center p-4 md:p-6 relative overflow-hidden">
      {/* Dynamic Background */}
      {event?.coverUrl && (
        <div 
          className="absolute inset-0 z-0 opacity-20 bg-cover bg-center blur-md"
          style={{ backgroundImage: `url(${event.coverUrl})` }}
        ></div>
      )}
      <div className="absolute inset-0 z-0 bg-gradient-to-t from-gray-900 via-gray-900/80 to-transparent"></div>

      {/* Tabs Switcher */}
      <div className="relative z-10 w-full max-w-md mb-6 flex bg-gray-800/80 backdrop-blur-xl rounded-full p-1 border border-gray-700/50 shadow-xl">
        <button
          onClick={() => setActiveTab('upload')}
          className={`flex-1 py-3 rounded-full text-sm font-bold flex items-center justify-center gap-2 transition-all ${
            activeTab === 'upload' ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'
          }`}
        >
          <Camera size={18} /> Subir Foto
        </button>
        <button
          onClick={() => setActiveTab('gallery')}
          className={`flex-1 py-3 rounded-full text-sm font-bold flex items-center justify-center gap-2 transition-all ${
            activeTab === 'gallery' ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'
          }`}
        >
          <ImageIcon size={18} /> Galería en Vivo
        </button>
      </div>

      <div className="relative z-10 w-full max-w-md">
        {activeTab === 'upload' ? (
          <div className="bg-gray-800/60 backdrop-blur-xl border border-gray-700/50 rounded-3xl shadow-2xl p-6 sm:p-8 animate-in fade-in slide-in-from-bottom-4 duration-300">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent mb-2">
                Comparte tu momento
              </h1>
              <p className="text-gray-400 text-sm">
                {event ? `en ${event.title}` : 'Cargando evento...'}
              </p>
            </div>

            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center py-8 space-y-4 animate-in fade-in zoom-in duration-500">
                <div className="h-20 w-20 bg-green-500/20 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-10 w-10 text-green-400" />
                </div>
                <h2 className="text-xl font-semibold text-white">¡Foto enviada!</h2>
                <p className="text-gray-400 text-center text-sm px-4">
                  Tu foto fue recibida correctamente. En breve aparecerá en la pantalla gigante y en la Galería.
                </p>
                <div className="flex gap-3 mt-4 w-full px-2">
                  <button
                    onClick={() => setActiveTab('gallery')}
                    className="flex-1 py-3 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl transition-colors backdrop-blur-sm text-sm"
                  >
                    Ver Galería
                  </button>
                  <button
                    onClick={resetUpload}
                    className="flex-1 py-3 bg-purple-600 hover:bg-purple-500 text-white font-bold rounded-xl transition-colors backdrop-blur-sm text-sm"
                  >
                    Subir otra foto
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleUpload} className="space-y-6">
                <div className="relative group">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                    id="photo-upload"
                  />
                  <label
                    htmlFor="photo-upload"
                    className={`flex flex-col items-center justify-center w-full aspect-[4/5] rounded-2xl border-2 border-dashed transition-all cursor-pointer overflow-hidden ${
                      preview ? 'border-transparent' : 'border-gray-600 hover:border-purple-500 bg-gray-900/50 hover:bg-gray-800/50'
                    }`}
                  >
                    {preview ? (
                      <div className="relative w-full h-full">
                        <img src={preview} alt="Preview" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center backdrop-blur-sm">
                          <Camera className="h-8 w-8 text-white mb-2" />
                          <span className="text-white font-medium">Cambiar foto</span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center p-6 text-center space-y-4">
                        <div className="p-4 bg-gray-800 rounded-full group-hover:bg-gray-700 transition-colors">
                          <Camera className="h-8 w-8 text-gray-400 group-hover:text-purple-400 transition-colors" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-200">Toca para abrir la cámara</p>
                          <p className="text-xs text-gray-500 mt-1">o selecciona una foto de tu galería</p>
                        </div>
                      </div>
                    )}
                  </label>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-400 ml-1">Tu nombre (opcional)</label>
                  <input
                    type="text"
                    value={uploadedBy}
                    onChange={(e) => setUploadedBy(e.target.value)}
                    placeholder="Ej. Martín"
                    className="w-full bg-gray-900/50 border border-gray-700/50 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all"
                    maxLength={30}
                  />
                </div>

                {status === 'error' && (
                  <div className="flex items-center space-x-2 text-red-400 text-sm bg-red-400/10 p-3 rounded-lg border border-red-400/20">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    <span>{errorMsg}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!file || status === 'uploading'}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:from-gray-700 disabled:to-gray-700 disabled:text-gray-400 disabled:cursor-not-allowed text-white font-semibold py-4 rounded-xl shadow-lg hover:shadow-purple-500/25 transition-all flex items-center justify-center space-x-2"
                >
                  {status === 'uploading' ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Subiendo...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="h-5 w-5" />
                      <span>Subir Foto</span>
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        ) : (
          /* GALLERY VIEW */
          <div className="bg-gray-800/60 backdrop-blur-xl border border-gray-700/50 rounded-3xl shadow-2xl p-6 sm:p-8 min-h-[500px] flex flex-col animate-in fade-in slide-in-from-bottom-4 duration-300">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-white mb-1">Fotos del Evento</h2>
              <p className="text-gray-400 text-xs text-balance">Las imágenes son revisadas por el organizador y aparecen aquí en vivo.</p>
            </div>
            
            {loadingGallery && galleryPhotos.length === 0 ? (
              <div className="flex-1 flex justify-center items-center py-12">
                <Loader2 className="animate-spin text-purple-500" size={32} />
              </div>
            ) : galleryPhotos.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center py-12 text-gray-400">
                <div className="w-16 h-16 bg-gray-800 rounded-2xl flex items-center justify-center mb-4 border border-gray-700">
                  <ImageIcon size={32} className="opacity-50" />
                </div>
                <p className="font-semibold text-white/70">Aún no hay fotos públicas.</p>
                <p className="text-sm mt-1 mb-6">Demostrá tu estilo y sé el primero.</p>
                <button
                  onClick={() => setActiveTab('upload')}
                  className="px-6 py-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors backdrop-blur-sm text-sm font-bold"
                >
                  Subir mi foto
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 overflow-y-auto max-h-[55vh] pr-2 custom-scrollbar">
                {galleryPhotos.map((photo) => (
                  <div 
                    key={photo._id} 
                    className="aspect-square bg-gray-900 rounded-xl overflow-hidden relative group cursor-pointer border border-white/5 hover:border-purple-500/50 transition-colors" 
                    onClick={() => setSelectedPhoto(photo)}
                  >
                    <img src={photo.url} loading="lazy" alt="Event" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
                       <span className="text-[10px] text-white/80 truncate font-medium"><Download size={10} className="inline mr-1" />Guardar</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer Branding */}
      <div className="relative z-10 mt-8 text-center text-gray-500 text-xs">
        Desarrollado por PASSAR
      </div>

      {/* Fullscreen Photo View for Downloading */}
      {selectedPhoto && (
        <div className="fixed inset-0 z-50 bg-black/95 flex flex-col items-center justify-center p-4 backdrop-blur-md animate-in fade-in zoom-in-95 duration-200">
          <button 
            onClick={() => setSelectedPhoto(null)}
            className="absolute top-6 right-6 p-2 bg-white/10 hover:bg-red-500/50 hover:text-white rounded-full text-white/70 transition-all z-10"
          >
            <X size={24} />
          </button>
          
          <div className="relative max-w-2xl w-full flex-1 flex flex-col items-center justify-center">
            <img src={selectedPhoto.url} alt="Fullscreen" className="max-w-full max-h-[70vh] object-contain rounded-2xl shadow-2xl" />
            
            <div className="w-full max-w-sm bg-gray-900/80 backdrop-blur-xl border border-gray-700/50 mt-6 p-4 rounded-2xl flex items-center justify-between">
              <div className="flex flex-col">
                 <span className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">Subida por</span>
                 <span className="text-white font-bold truncate max-w-[120px]">{selectedPhoto.uploadedBy || 'Anónimo'}</span>
              </div>
              <a 
                href={selectedPhoto.url} 
                download={`passar-foto-${selectedPhoto._id}.jpg`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white rounded-xl font-bold text-sm transition-all shadow-lg hover:shadow-purple-500/25"
              >
                <Download size={16} /> Descargar
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuestUpload;
